import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  useTheme
} from '@material-ui/core/styles';
import { KeyboardDatePicker } from '../CustomDatePicker';
import moment from 'moment';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
  BulkDocument,
  BulkDocumentRevision
} from '../../common/interface/bulkPlanning';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../constants';
import { addWeekdays } from '../../functions/addWeekdays';
import AutoAwesomeRounded from '../../icon/AutoAwesomeRounded';
import HelpTooltip from '../HelpTooltip';

interface PurposeRevision {
  purposeId: number | null;
  revisionId: number | null;
  manHour: number | null;
  planIssueDate: Date | null;
  planCompletedDate: Date | null;
}

interface BulkUpdateDateProps<T> {
  documents: T[];
  setDocuments: Dispatch<SetStateAction<T[]>>;
  documentRefIds: number[];
  setDocumentRefIds: Dispatch<SetStateAction<number[]>>;
  show: boolean;
}

export default function BulkUpdateDetail<T extends BulkDocument>({
  documents,
  setDocuments,
  documentRefIds,
  setDocumentRefIds,
  show
}: BulkUpdateDateProps<T>) {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { purposes, revisions } = useSelector((state: RootState) => ({
    purposes: state.purposes.data,
    revisions: state.revisions.data
  }));

  const [open, setOpen] = useState<boolean>(false);
  const [purposeRevisions, setPurposeRevisions] = useState<PurposeRevision[]>(
    []
  );

  const uniquePurposeRevisions = useMemo(() => {
    return Array.from(
      new Map(
        documents
          .filter(({ refId }) => new Set(documentRefIds).has(refId))
          .flatMap(({ projectDocumentRevisions }) =>
            projectDocumentRevisions.map(({ purposeId, revisionId }) => ({
              purposeId,
              revisionId
            }))
          )
          .map((rev) => [`${rev.purposeId}-${rev.revisionId}`, rev])
      ).values()
    ).sort((a, b) => {
      if (a.revisionId == null) return 1;
      if (b.revisionId == null) return -1;
      return a.revisionId - b.revisionId;
    });
  }, [documents, documentRefIds]);

  useEffect(() => {
    if (show && open) {
      const purposeRevisions = uniquePurposeRevisions.map((rev) => ({
        ...rev,
        manHour: null,
        planIssueDate: null,
        planCompletedDate: null
      }));
      setPurposeRevisions(purposeRevisions);
    }
  }, [uniquePurposeRevisions, open, show]);

  function getValue<T extends { id: number }>(
    id: number | null,
    options: T[]
  ): T | null {
    return options.find((option) => option.id === id) ?? null;
  }

  function handleOpen() {
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  function handleChangeDetail<K extends keyof BulkDocumentRevision>(
    value: BulkDocumentRevision[K],
    field: K,
    index: number,
    options?: { positiveNumber: boolean }
  ): void {
    setPurposeRevisions((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updatedValue =
          options?.positiveNumber && typeof value === 'number'
            ? value <= 0
              ? null
              : value
            : value;

        const newItem = {
          ...item,
          [field]: updatedValue
        };

        if (field === 'planIssueDate') {
          newItem.planCompletedDate =
            value instanceof Date
              ? addWeekdays(value, DAYS_FOR_COMPLETE_REVIEW)
              : null;
        }

        return newItem;
      })
    );
  }

  function onUpdate() {
    setDocuments((prevDocuments) =>
      prevDocuments.map((document) => {
        // Only update documents with refId in documentRefIds
        if (!documentRefIds.includes(document.refId)) {
          return document;
        }

        const updatedRevisions = document.projectDocumentRevisions.map(
          (projectDocumentRevision) => {
            const match = purposeRevisions.find(
              (purposeRevision) =>
                purposeRevision.purposeId ===
                  projectDocumentRevision.purposeId &&
                purposeRevision.revisionId ===
                  projectDocumentRevision.revisionId
            );

            if (!match) {
              return projectDocumentRevision;
            }

            const { manHour, planIssueDate, planCompletedDate } = match;

            // Skip update if all values are null
            const noUpdate =
              manHour === null &&
              planIssueDate === null &&
              planCompletedDate === null;

            if (noUpdate) {
              return projectDocumentRevision;
            }

            return {
              ...projectDocumentRevision,
              ...(manHour !== null && { manHour }),
              ...(planIssueDate !== null && { planIssueDate }),
              ...(planCompletedDate !== null && { planCompletedDate })
            };
          }
        );

        return {
          ...document,
          projectDocumentRevisions: updatedRevisions
        };
      })
    );

    setDocumentRefIds([]);
    handleClose();
  }

  return (
    <>
      <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
        <DialogTitle className={classes.pb0}>
          {t('bulkUpdateDetail.title')}
        </DialogTitle>
        <DialogContent>
          {t('bulkUpdateDetail.foundRevisions', {
            revisionCount: purposeRevisions.length,
            documentCount: documentRefIds.length
          })}
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t('bulkUpdateDetail.headers.manHour')}</TableCell>
                <TableCell>{t('bulkUpdateDetail.headers.issueDate')}</TableCell>
                <TableCell>
                  {t('bulkUpdateDetail.headers.completedDate')}
                  <HelpTooltip>
                    {t('bulkUpdateDetail.tooltips.completedDateCalculation')}
                  </HelpTooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purposeRevisions.map(
                (
                  {
                    purposeId,
                    revisionId,
                    manHour,
                    planIssueDate,
                    planCompletedDate
                  },
                  index
                ) => {
                  return (
                    <TableRow
                      className={classes.unsetAll}
                      key={`${purposeId}-${revisionId}`}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {t('bulkUpdateDetail.labels.purpose')}{' '}
                          {getValue(purposeId, purposes)?.abbreviation ?? '-'}{' '}
                          {t('bulkUpdateDetail.labels.revision')}{' '}
                          {getValue(revisionId, revisions)?.name ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={manHour ? manHour : ''}
                          onChange={(event) => {
                            const parsedValue = Number.parseFloat(
                              event.target.value
                            );
                            const value = isNaN(parsedValue)
                              ? null
                              : parsedValue;
                            handleChangeDetail(value, 'manHour', index, {
                              positiveNumber: true
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <KeyboardDatePicker
                          autoOk
                          size="small"
                          variant="inline"
                          InputAdornmentProps={{
                            position: 'end'
                          }}
                          value={planIssueDate}
                          onChange={(event) =>
                            handleChangeDetail(
                              event
                                ? moment(event).endOf('day').toDate()
                                : null,
                              'planIssueDate',
                              index
                            )
                          }
                          KeyboardButtonProps={{
                            'aria-label': t('bulkUpdateDetail.ariaLabels.changePlanIssueDate') as string
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <KeyboardDatePicker
                          autoOk
                          size="small"
                          variant="inline"
                          InputAdornmentProps={{
                            position: 'end'
                          }}
                          value={planCompletedDate}
                          onChange={(event) =>
                            handleChangeDetail(
                              event
                                ? moment(event).endOf('day').toDate()
                                : null,
                              'planCompletedDate',
                              index
                            )
                          }
                          KeyboardButtonProps={{
                            'aria-label': t('bulkUpdateDetail.ariaLabels.changePlanCompletedDate') as string
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={onUpdate}
            fullWidth
          >
            {t('bulkUpdateDetail.buttons.updateAll')}
          </Button>
          <Button onClick={handleClose} fullWidth>
            {t('bulkUpdateDetail.buttons.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <div className={classes.fab}>
        <Zoom
          in={show && !open && documentRefIds.length > 0}
          timeout={transitionDuration}
          style={{
            transitionDelay: `${transitionDuration.exit}ms`
          }}
          unmountOnExit
        >
          <Fab variant="extended" color="primary" onClick={handleOpen}>
            <AutoAwesomeRounded className={classes.extendedIcon} />
            {t('bulkUpdateDetail.buttons.updateDetails')}
          </Fab>
        </Zoom>
      </div>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fab: {
      position: 'fixed',
      bottom: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 100,
      display: 'flex',
      gap: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      }
    },
    extendedIcon: {
      marginRight: theme.spacing(0.5)
    },
    unsetAll: {
      '& > *': {
        borderBottom: 'unset'
      }
    },
    flexGrow: {
      flexGrow: 1
    },
    pb0: {
      paddingBottom: 0
    }
  })
);
